import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MessageEnvelope,
  getQueryTypeStarttoken,
  buildGroupedMessages,
  getVisibleMessages,
  mergeAndSortMessages,
  TIMETOKEN_MULTIPLIER,
  getGrowGroupRanges,
  MessageListItem,
} from '@zonofi/common';
import { usePrevious } from './use-previous';
import {
  dispatch,
  getQueryThread,
  getWorkspaceId,
  useAppSelector,
  useLazyGetMessageHistoryQuery,
  getChannelMessages,
  getCurrentChannelPagination,
  updateMessagesByChannelId,
  updateChannelPagination,
  updateScrollToBottom,
  getIsScrollToBottom,
} from '@zonofi/zono-money-store';

interface FetchHistoryOptions {
  resetExisting?: boolean;
  endTimeToken?: string;
}

export const useGetConversationHistory = () => {
  // Apis
  const [fetchMsgHistory] = useLazyGetMessageHistoryQuery();

  // Selectors
  const queryThread = useAppSelector(getQueryThread);

  const goToBottom = useAppSelector(getIsScrollToBottom);
  const messages = useAppSelector(
    getChannelMessages(String(queryThread?.channelId))
  ) as MessageEnvelope[];
  const workspaceId = useAppSelector(getWorkspaceId);
  const paginationEnd = useAppSelector(
    getCurrentChannelPagination(String(queryThread?.channelId))
  );
  const growQueryConversationThread = useMemo(
    () => ({
      channelId: String(queryThread?.channelId ?? ''),
      startTimeToken: String(queryThread?.startMessageTimetoken ?? ''),
      endTimeToken: String(queryThread?.endMessageTimetoken ?? ''),
      isGrowConversation: true,
      conversationId: String(queryThread?.conversationId ?? ''),
      backgroundColor: queryThread?.backgroundColor ?? '#7f2cf4',
      queryType: queryThread?.queryType ?? '',
    }),
    [queryThread]
  );

  // Previous values
  const prevChannelId = usePrevious(String(queryThread?.channelId));
  const prevMessages = usePrevious(messages);

  const hasUsedGrowTokenRef = useRef<boolean>(false);

  // Always reflects the latest growQueryConversationThread as of the current render
  const growQueryConversationThreadRef = useRef(growQueryConversationThread);
  growQueryConversationThreadRef.current = growQueryConversationThread;

  // Local state
  const [scrolledBottom, setScrolledBottom] = useState(true);
  const [initMessagesLoaded, setInitMessagesLoaded] = useState<
    Record<string, boolean>
  >({});
  const [fetchingMessages, setFetchingMessages] = useState(false);

  // Memoized dispatchers
  const setMessages = useCallback(
    (messages: MessageEnvelope[]) => {
      dispatch(
        updateMessagesByChannelId({
          channelId: String(queryThread?.channelId),
          messages,
        })
      );
    },
    [queryThread?.channelId]
  );

  const setPaginationEnd = useCallback(
    (value: boolean) => {
      dispatch(
        updateChannelPagination({
          channelId: String(queryThread?.channelId),
          pagination: value,
        })
      );
    },
    [queryThread?.channelId]
  );

  const activeMessages = useMemo(() => {
    return getVisibleMessages(messages, paginationEnd);
  }, [messages, paginationEnd]);

  const growGroupRanges = useMemo(() => {
    return getGrowGroupRanges(messages, growQueryConversationThread);
  }, [growQueryConversationThread, messages]);

  const growConversationId = growQueryConversationThread?.isGrowConversation
    ? (growQueryConversationThread.conversationId ?? '')
    : '';
  const prevGrowConversationId = usePrevious(growConversationId);

  const groupedMessages: MessageListItem[] = useMemo(() => {
    return buildGroupedMessages(activeMessages, growGroupRanges);
  }, [activeMessages, growGroupRanges]);

  // Scroll handlers
  const toBottom = useCallback(() => {
    setScrolledBottom(true);
    dispatch(updateScrollToBottom(false));
  }, []);

  const startTimeToken = useMemo(() => {
    if (!hasUsedGrowTokenRef.current) {
      const growFetchStartToken = getQueryTypeStarttoken(
        growQueryConversationThread
      );
      if (growFetchStartToken) {
        return growFetchStartToken;
      }
    }
    const oldestMessage = activeMessages.find((m) => typeof m === 'object') as
      | MessageEnvelope
      | undefined;
    return (oldestMessage?.timetoken as string) || undefined;
  }, [growQueryConversationThread, activeMessages]);

  // Fetch message history
  const fetchHistory = useCallback(
    async (options?: FetchHistoryOptions) => {
      if (fetchingMessages) return null;

      const currentGrowThread = growQueryConversationThreadRef.current;
      const isGrowConversation = !!currentGrowThread?.isGrowConversation;
      const shouldResetExisting = !!options?.resetExisting;
      const endTimeTokenOverride = options?.endTimeToken;
      const isForwardPagination = !!endTimeTokenOverride;
      const existingMessagesForMerge = shouldResetExisting ? [] : messages;
      const growFetchStartToken = getQueryTypeStarttoken(currentGrowThread);
      const effectiveStartTimeToken =
        isGrowConversation && !hasUsedGrowTokenRef.current
          ? growFetchStartToken
          : startTimeToken;

      const shouldFetchMessages =
        isForwardPagination ||
        (isGrowConversation && !hasUsedGrowTokenRef.current) ||
        !paginationEnd;

      if (!shouldFetchMessages) return null;

      setFetchingMessages(true);
      try {
        const response = await fetchMsgHistory({
          workspaceId: workspaceId || '',
          channelId: String(queryThread?.channelId),
          startTimeToken: endTimeTokenOverride || '0',
          endTimeToken: endTimeTokenOverride
            ? String(Date.now() * TIMETOKEN_MULTIPLIER)
            : effectiveStartTimeToken ||
              String(Date.now() * TIMETOKEN_MULTIPLIER),
          limit: 25,
        }).unwrap();

        const newMessages: MessageEnvelope[] = response.data || [];

        if (!isForwardPagination) {
          setPaginationEnd(
            !response.pagination?.nextKey && newMessages.length < 25
          );
        }

        const allMessages = mergeAndSortMessages(
          existingMessagesForMerge,
          newMessages
        );

        if (isGrowConversation && !hasUsedGrowTokenRef.current) {
          hasUsedGrowTokenRef.current = true;
        }

        setMessages(allMessages);
        return newMessages.length;
      } catch (error) {
        console.error('Failed to fetch history:', error);
        return undefined;
      } finally {
        setFetchingMessages(false);
      }
    },
    [
      paginationEnd,
      fetchingMessages,
      queryThread?.channelId,
      messages,
      workspaceId,
      fetchMsgHistory,
      setPaginationEnd,
      setMessages,
      startTimeToken,
      growQueryConversationThread?.isGrowConversation,
    ]
  );

  // Initialize messages on channel change
  useEffect(() => {
    if (!String(queryThread?.channelId)) return;

    const isGrowConversation =
      !!growQueryConversationThread?.isGrowConversation;
    const hasChannelChanged = String(queryThread?.channelId) !== prevChannelId;
    const hasGrowConversationWindowChanged =
      isGrowConversation &&
      !!growConversationId &&
      growConversationId !== prevGrowConversationId;

    if (!hasChannelChanged && !hasGrowConversationWindowChanged) return;

    setPaginationEnd(false);

    if (hasGrowConversationWindowChanged) {
      hasUsedGrowTokenRef.current = false;
      setMessages([]);
    }

    if (
      isGrowConversation ||
      !initMessagesLoaded[String(queryThread?.channelId)] ||
      hasGrowConversationWindowChanged
    ) {
      fetchHistory({
        resetExisting: hasGrowConversationWindowChanged,
      })
        .then(() => {
          setInitMessagesLoaded((curr) => ({
            ...curr,
            [String(queryThread?.channelId)]: true,
          }));
        })
        .catch((e) => console.error('Failed to initialize messages:', e));
    }
  }, [
    queryThread?.channelId,
    growConversationId,
    growQueryConversationThread,
    prevChannelId,
    prevGrowConversationId,
    initMessagesLoaded,
    fetchHistory,
    setMessages,
    setPaginationEnd,
  ]);

  useEffect(() => {
    hasUsedGrowTokenRef.current = false;
  }, [queryThread?.channelId, growConversationId]);

  return {
    messages,
    prevChannelId,
    scrolledBottom,
    setScrolledBottom,
    groupedMessages,
    initMessagesLoaded,
    fetchHistory,
    fetchingMessages,
    hasNewMessages: (prevMessages?.length || 0) !== messages.length,
    goToBottom,
    toBottom,
    allPaginationEnded: paginationEnd,
    channelId: String(queryThread?.channelId),
    conversationId: String(queryThread?.conversationId),
    growQueryConversationThread,
  };
};
